import React, { lazy, Suspense, useEffect } from 'react';
import LoginOtp from "./LoginOtp";
import Layout from "./Layout.jsx";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { useAuth } from "@/auth/AuthContext";
import { readAuthToken } from "@/lib/authStorage";
import { CategoryFilterProvider } from "@/contexts/CategoryFilterContext";
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';

// תפיסת שגיאת טעינת chunk (למשל באופליין) — מסך ידידותי במקום קריסה
class ChunkLoadErrorBoundary extends React.Component {
  state = { hasChunkError: false };
  static getDerivedStateFromError(error) {
    if (error?.message?.includes?.('Failed to fetch dynamically imported module')) return { hasChunkError: true };
    throw error; // שגיאות אחרות — להעביר ל־Error Boundary החיצוני (Sentry)
  }
  componentDidCatch() {}
  render() {
    if (this.state.hasChunkError) {
      return (
        <div style={{ padding: 24, textAlign: 'center', maxWidth: 400, margin: '40px auto' }}>
          <p style={{ marginBottom: 16 }}>הדף לא נטען — כנראה אין חיבור לאינטרנט והדף לא נשמר במטמון.</p>
          <p style={{ marginBottom: 16, fontSize: 14, color: '#666' }}>חבר לאינטרנט ולחץ רענן.</p>
          <button
            type="button"
            onClick={() => { if (typeof navigator !== 'undefined' && navigator.onLine) window.location.reload(); }}
            style={{ padding: '10px 20px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer' }}
          >
            רענן שוב
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

// טעינה בשקט של chunks קריטיים כשהאפליקציה נטענת (אונליין) — כדי שה-SW ישמור אותם לריענון באופליין
function PrefetchCriticalChunks() {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.onLine) return;
    import('./Home').catch(() => {});
    import('./Cart').catch(() => {});
  }, []);
  return null;
}

const Home = lazy(() => import("./Home"));
const Categories = lazy(() => import("./Categories"));
const Companies = lazy(() => import("./Companies"));
const Suppliers = lazy(() => import("./Suppliers"));
const Promotions = lazy(() => import("./Promotions"));
const Recommendations = lazy(() => import("./Recommendations"));
const MustOrder = lazy(() => import("./MustOrder"));
const NewProducts = lazy(() => import("./NewProducts"));
const ServiceRequests = lazy(() => import("./ServiceRequests"));
const DailyInventory = lazy(() => import("./DailyInventory"));
const ManagementInventory = lazy(() => import("./ManagementInventory"));
const Notifications = lazy(() => import("./Notifications"));
const Destructions = lazy(() => import("./Destructions"));
const Returns = lazy(() => import("./Returns"));
const Cart = lazy(() => import("./Cart"));
const ProductDetails = lazy(() => import("./ProductDetails"));
const CategoryProducts = lazy(() => import("./CategoryProducts.jsx"));
const CompanyProducts = lazy(() => import("./CompanyProducts"));
const SupplierProducts = lazy(() => import("./SupplierProducts"));
const InventoryCount = lazy(() => import("./InventoryCount"));
const Orders = lazy(() => import("./Orders"));
const OrderDetails = lazy(() => import("./OrderDetails"));
const ReceiveGoods = lazy(() => import("./ReceiveGoods"));
const ReceiveGoodsDetails = lazy(() => import("./ReceiveGoodsDetails"));
const Brochures = lazy(() => import("./Brochures"));
const PromotionImplementation = lazy(() => import("./PromotionImplementation"));
const ServiceRequestDetails = lazy(() => import("./ServiceRequestDetails"));
const DestructionDetails = lazy(() => import("./DestructionDetails"));
const ReturnDetails = lazy(() => import("./ReturnDetails"));
const SupplierRecommendations = lazy(() => import("./SupplierRecommendations"));
const Users = lazy(() => import("./Users"));
const UserDetails = lazy(() => import("./UserDetails"));
const SlowMovingProducts = lazy(() => import("./SlowMovingProducts"));
const Stores = lazy(() => import("./Stores"));
const StoreDetails = lazy(() => import("./StoreDetails"));
const Chains = lazy(() => import("./Chains"));
const MustOrderStores = lazy(() => import("./MustOrderStores"));
const AllSlowMovingProducts = lazy(() => import("./AllSlowMovingProducts"));
const SearchedNotInWarehouse = lazy(() => import("./SearchedNotInWarehouse"));
const AllReturns = lazy(() => import("./AllReturns"));
const AllDestructions = lazy(() => import("./AllDestructions"));
const Products = lazy(() => import("./Products"));
const ProductForm = lazy(() => import("./ProductForm"));
const StoreProducts = lazy(() => import("./StoreProducts.jsx"));
const StoreRecommendations = lazy(() => import("./StoreRecommendations"));
const CreateMustOrderTask = lazy(() => import("./CreateMustOrderTask"));
const ManageInventoryCounts = lazy(() => import("./ManageInventoryCounts"));
const CreateInventoryCount = lazy(() => import("./CreateInventoryCount"));
const SupplierDetails = lazy(() => import("./SupplierDetails"));
const StoresByChainPromotions = lazy(() => import("./StoresByChainPromotions"));
const PromotionsByStore = lazy(() => import("./PromotionsByStore"));
const SafeCount = lazy(() => import("./SafeCount"));
const SafeCountAdmin = lazy(() => import("./SafeCountAdmin"));
const SystemSettings = lazy(() => import("./SystemSettings"));
const CartManagement = lazy(() => import("./CartManagement"));
const PublicOrderView = lazy(() => import("./PublicOrderView"));
const ManagementTasks = lazy(() => import("./ManagementTasks"));
const ManagementTaskDetails = lazy(() => import("./ManagementTaskDetails"));
const StoreManagementTasks = lazy(() => import("./StoreManagementTasks"));
const CreateManagementTask = lazy(() => import("./CreateManagementTask"));
const CompleteManagementTask = lazy(() => import("./CompleteManagementTask"));
const BasicProductsReminder = lazy(() => import("./BasicProductsReminder"));
const AdminTerminal = lazy(() => import("./AdminTerminal"));
const PrefetchOrder = lazy(() => import("./PrefetchOrder"));
const CriticalData = lazy(() => import("./CriticalData"));
const AgentPanel = lazy(() => import("./AgentPanel"));
const StoreView = lazy(() => import("./StoreView"));

const PAGES = {
    Home: Home,
    Categories: Categories,
    Companies: Companies,
    Suppliers: Suppliers,
    Promotions: Promotions,
    Recommendations: Recommendations,
    MustOrder: MustOrder,
    NewProducts: NewProducts,
    ServiceRequests: ServiceRequests,
    DailyInventory: DailyInventory,
    ManagementInventory: ManagementInventory,
    Notifications: Notifications,
    Destructions: Destructions,
    Returns: Returns,
    Cart: Cart,
    ProductDetails: ProductDetails,
    CategoryProducts: CategoryProducts,
    CompanyProducts: CompanyProducts,
    SupplierProducts: SupplierProducts,
    InventoryCount: InventoryCount,
    Orders: Orders,
    OrderDetails: OrderDetails,
    ReceiveGoods: ReceiveGoods,
    ReceiveGoodsDetails: ReceiveGoodsDetails,
    Brochures: Brochures,
    PromotionImplementation: PromotionImplementation,
    ServiceRequestDetails: ServiceRequestDetails,
    DestructionDetails: DestructionDetails,
    ReturnDetails: ReturnDetails,
    SupplierRecommendations: SupplierRecommendations,
    Users: Users,
    UserDetails: UserDetails,
    SlowMovingProducts: SlowMovingProducts,
    Stores: Stores,
    StoreDetails: StoreDetails,
    Chains: Chains,
    MustOrderStores: MustOrderStores,
    AllSlowMovingProducts: AllSlowMovingProducts,
    SearchedNotInWarehouse: SearchedNotInWarehouse,
    AllReturns: AllReturns,
    AllDestructions: AllDestructions,
    Products: Products,
    ProductForm: ProductForm,
    StoreProducts: StoreProducts,
    StoreRecommendations: StoreRecommendations,
    CreateMustOrderTask: CreateMustOrderTask,
    ManageInventoryCounts: ManageInventoryCounts,
    CreateInventoryCount: CreateInventoryCount,
    SupplierDetails: SupplierDetails,
    StoresByChainPromotions: StoresByChainPromotions,
    PromotionsByStore: PromotionsByStore,
    SafeCount: SafeCount,
    SafeCountAdmin: SafeCountAdmin,
    ManagementTasks: ManagementTasks,
  ManagementTaskDetails: ManagementTaskDetails,
  StoreManagementTasks: StoreManagementTasks,
  CreateManagementTask: CreateManagementTask,
    CompleteManagementTask: CompleteManagementTask,
    CriticalData: CriticalData,
}

function _getCurrentPage(url) {
    if (url.endsWith('/')) {
        url = url.slice(0, -1);
    }
    let urlLastPart = url.split('/').pop();
    if (urlLastPart.includes('?')) {
        urlLastPart = urlLastPart.split('?')[0];
    }

    const pageName = Object.keys(PAGES).find(page => page.toLowerCase() === urlLastPart.toLowerCase());
    return pageName || Object.keys(PAGES)[0];
}

// זה הרכיב ששומר עלינו. אם אין טוקן - הוא זורק ללוגין
function ProtectedRoute({ children }) {
    const token = readAuthToken();

    if (!token) {
        // שימוש ב-Navigate עדיף על window.location כי הוא לא מרענן את כל הדף
        return <Navigate to="/login" replace />;
    }

    return children;
}

function PagesContent() {
    const { user } = useAuth();
    const location = useLocation();

    // חריגה: אם אנחנו כבר בנתיב של לוגין, תציג אותו בלי Layout ובלי הגנות
    if (location.pathname === "/login") {
        return (
            <Routes>
                <Route path="/login" element={<LoginOtp />} />
            </Routes>
        );
    }

    if (location.pathname.startsWith("/view-order/")) {
        return (
            <Routes>
                <Route path="/view-order/:id" element={<PublicOrderView />} />
            </Routes>
        );
    }

    const currentPage = _getCurrentPage(location.pathname);
    const isAdmin = user?.isSuperAdmin || user?.role?.key === 'SYSTEM_ADMIN' || user?.role?.key === 'SUPER_ADMIN';
    const isSuperAdminLevel =
        user?.isSuperAdmin ||
        user?.role?.key === 'SUPER_ADMIN' ||
        user?.role?.key === 'SYSTEM_ADMIN' ||
        user?.custom_role === 'system_admin';

    const canAccessCriticalData =
        isSuperAdminLevel && !!user?.criticalDataAccess;

    const roleKey = user?.role?.key || user?.role;
const isPromotionsManager = roleKey === 'PROMOTIONS_ADMIN';
const isAgent = roleKey === 'AGENT' || user?.custom_role === 'AGENT';
const isImpersonating = !!user?.is_impersonating;

// סוכן — רואה רק את לוח הסוכן (אלא אם כן הוא נמצא במצב התחזות לחנות)
if (isAgent && !isImpersonating) {
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/AgentPanel" element={<AgentPanel />} />
                        <Route path="*" element={<Navigate to="/AgentPanel" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

// אם המשתמשת היא מנהלת מבצעים, אנחנו מגדירים רשימת נתיבים מצומצמת מאוד
if (isPromotionsManager) {
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/Promotions" element={<Promotions />} />
                        <Route path="*" element={<Navigate to="/Promotions" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

const isMerlogManager = roleKey === 'MERLOG_MANAGER';

if (isMerlogManager) {
    return (
        <ProtectedRoute>
            <ChunkLoadErrorBoundary>
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                        <Route path="/Suppliers" element={<Suppliers />} />
                        <Route path="/SupplierDetails" element={<SupplierDetails />} />
                        <Route path="/Orders" element={<Orders />} />
                        <Route path="/OrderDetails" element={<OrderDetails />} />
                        <Route path="/Stores" element={<Stores />} />
                        <Route path="/StoreDetails" element={<StoreDetails />} />
                        <Route path="/" element={<Navigate to="/Suppliers" replace />} />
                        <Route path="/Home" element={<Navigate to="/Suppliers" replace />} />
                        <Route path="*" element={<Navigate to="/Suppliers" replace />} />
                    </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
        </ProtectedRoute>
    );
}

    return (
        <ProtectedRoute>
            <CategoryFilterProvider>
            <ChunkLoadErrorBoundary>
            <PrefetchCriticalChunks />
            <Layout currentPageName={currentPage}>
                <Suspense fallback={<LoadingSpinner />}>
                    <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/Home" element={<Home />} />
                    <Route path="/prefetch-order" element={<PrefetchOrder />} />
                    <Route path="/Categories" element={<Categories />} />
                    <Route path="/Companies" element={<Companies />} />
                    <Route path="/Suppliers" element={<Suppliers />} />
                    <Route path="/Promotions" element={<Promotions />} />
                    <Route path="/Recommendations" element={<Recommendations />} />
                    <Route path="/MustOrder" element={<MustOrder />} />
                    <Route path="/NewProducts" element={<NewProducts />} />
                    <Route path="/ServiceRequests" element={<ServiceRequests />} />
                    <Route path="/DailyInventory" element={<DailyInventory />} />
                    <Route path="/ManagementInventory" element={<ManagementInventory />} />
                    <Route path="/Notifications" element={<Notifications />} />
                    <Route path="/Destructions" element={<Destructions />} />
                    <Route path="/Returns" element={<Returns />} />
                    <Route path="/Cart" element={<Cart />} />
                    <Route path="/ProductDetails" element={<ProductDetails />} />
                    <Route path="/CategoryProducts" element={<CategoryProducts />} />
                    <Route path="/CompanyProducts" element={<CompanyProducts />} />
                    <Route path="/SupplierProducts" element={<SupplierProducts />} />
                    <Route path="/InventoryCount" element={<InventoryCount />} />
                    <Route path="/Orders" element={<Orders />} />
                    <Route path="/OrderDetails" element={<OrderDetails />} />
                    <Route path="/ReceiveGoods" element={<ReceiveGoods />} />
                    <Route path="/ReceiveGoodsDetails" element={<ReceiveGoodsDetails />} />
                    <Route path="/Brochures" element={<Brochures />} />
                    <Route path="/PromotionImplementation" element={<PromotionImplementation />} />
                    <Route path="/ServiceRequestDetails" element={<ServiceRequestDetails />} />
                    <Route path="/DestructionDetails" element={<DestructionDetails />} />
                    <Route path="/ReturnDetails" element={<ReturnDetails />} />
                    <Route path="/SupplierRecommendations" element={<SupplierRecommendations />} />
                    <Route path="/Users" element={<Users />} />
                    <Route path="/UserDetails" element={<UserDetails />} />
                    <Route path="/SlowMovingProducts" element={<SlowMovingProducts />} />
                    <Route path="/Stores" element={<Stores />} />
                    <Route path="/StoreDetails" element={<StoreDetails />} />
                    <Route path="/Chains" element={<Chains />} />
                    <Route path="/MustOrderStores" element={<MustOrderStores />} />
                    <Route path="/AllSlowMovingProducts" element={<AllSlowMovingProducts />} />
                    <Route path="/SearchedNotInWarehouse" element={<SearchedNotInWarehouse />} />
                    <Route path="/AllReturns" element={<AllReturns />} />
                    <Route path="/AllDestructions" element={<AllDestructions />} />
                    <Route path="/Products" element={<Products />} />
                    <Route path="/ProductForm" element={<ProductForm />} />
                    <Route path="/StoreProducts" element={<StoreProducts />} />
                    <Route path="/StoreRecommendations" element={<StoreRecommendations />} />
                    <Route path="/CreateMustOrderTask" element={<CreateMustOrderTask />} />
                    <Route path="/ManageInventoryCounts" element={<ManageInventoryCounts />} />
                    <Route path="/CreateInventoryCount" element={<CreateInventoryCount />} />
                    <Route path="/SupplierDetails" element={<SupplierDetails />} />
                    <Route path="/StoresByChainPromotions" element={<StoresByChainPromotions />} />
                    <Route path="/PromotionsByStore" element={<PromotionsByStore />} />
                    <Route path="/SafeCount" element={<SafeCount />} />
                    <Route path="/SafeCountAdmin" element={<SafeCountAdmin />} />
                    <Route 
                        path="/SystemSettings" 
                        element={isAdmin ? <SystemSettings /> : <Navigate to="/" replace />} 
                    />
                    <Route path="/cartmanagement" element={<CartManagement />} />
                    <Route path="/ManagementTasks" element={isAdmin ? <ManagementTasks /> : <Navigate to="/" replace />} />                    
                    <Route path="/ManagementTaskDetails" element={isAdmin ? <ManagementTaskDetails /> : <Navigate to="/" replace />} />
                    <Route path="/StoreManagementTasks" element={<StoreManagementTasks />} />
                    <Route path="/CreateManagementTask" element={isAdmin ? <CreateManagementTask /> : <Navigate to="/" replace />} />
                    <Route path="/CompleteManagementTask" element={<CompleteManagementTask />} />
                    <Route path="/BasicProductsReminder" element={<BasicProductsReminder />} />
                    <Route path="/basicproductsreminder" element={<BasicProductsReminder />} />
                    <Route path="/AgentPanel" element={<AgentPanel />} />
                    <Route 
                        path="/AdminTerminal" 
                        element={isAdmin ? <AdminTerminal /> : <Navigate to="/" replace />} 
                    />
                    <Route
                        path="/CriticalData"
                        element={canAccessCriticalData ? <CriticalData /> : <Navigate to="/Home" replace />}
                    />
                </Routes>
                </Suspense>
            </Layout>
            </ChunkLoadErrorBoundary>
            </CategoryFilterProvider>
        </ProtectedRoute>
    );
}

export default function Pages() {
    return (
        <Router>
            <PagesContent />
        </Router>
    );
}